import { useEffect, useState } from 'react';
import { User, Shield, Eye, Save, Trash2, Key, Smartphone, AlertTriangle, MapPin } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useAuthStore } from '@/store/authStore';
import { useGlobalToast } from '@/contexts/ToastContext';
import { SavedAddressManager } from '@/components/location/SavedAddressManager';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

export function CustomerProfilePage() {
  const { user, isAuthenticated } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'privacy'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });

  const [securityMethods] = useState([
    { id: '1', type: 'app', name: 'Authenticator App', enabled: true, lastUsed: new Date() },
    { id: '2', type: 'sms', name: 'SMS Verification', enabled: false, lastUsed: null }
  ]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const stats = {
    jobsPosted: 24,
    completedJobs: 18,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSave = async () => {
    try {
      success('Profile Updated', 'Your profile information has been saved successfully.');
      setIsEditing(false);
    } catch (err) {
      error('Update Failed', 'There was a problem saving your profile.');
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || ''
    });
    setIsEditing(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'privacy', label: 'Privacy', icon: Eye }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Profile' }]} />

        <div className="grid lg:grid-cols-12 gap-8 mt-8">
          {/* Left Column: Sidebar */}
          <div className="lg:col-span-3 space-y-6">
            <Card className="text-center p-6">
              <div className="w-24 h-24 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4 text-primary-600">
                <User className="w-10 h-10" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{user?.firstName} {user?.lastName}</h2>
              <Badge variant="verified" className="mb-6 mx-auto inline-flex">Customer</Badge>

              <div className="grid grid-cols-2 gap-4 border-t pt-4 text-left">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{stats.jobsPosted}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Jobs Posted</div>
                </div>
                <div className="text-center border-l">
                  <div className="text-xl font-bold text-emerald-600">{stats.completedJobs}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Completed</div>
                </div>
              </div>
            </Card>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
                    }`}
                >
                  <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-primary-500' : 'text-gray-400'}`} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right Column: Content */}
          <div className="lg:col-span-9 space-y-6">
            {activeTab === 'profile' && (
              <div className="animate-in fade-in duration-300 space-y-6">
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <CardHeader title="Personal Information" icon={<User className="w-5 h-5 text-gray-400" />} className="mb-0" />
                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
                          <Button size="sm" onClick={handleSave}><Save className="w-4 h-4 mr-2" /> Save</Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>Edit Profile</Button>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        disabled={!isEditing}
                        className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        disabled={!isEditing}
                        className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled={!isEditing}
                        className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={!isEditing}
                        className="w-full form-input rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                </Card>

                <Card>
                  <CardHeader title="Saved Addresses" icon={<MapPin className="w-5 h-5 text-gray-400" />} />
                  <div className="p-0">
                    <SavedAddressManager />
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="animate-in fade-in duration-300 space-y-6">
                <Card>
                  <CardHeader title="Security Settings" icon={<Shield className="w-5 h-5 text-gray-400" />} />
                  <div className="space-y-4">
                    {securityMethods.map((method) => (
                      <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center gap-3">
                          {method.type === 'app' ? <Key className="w-5 h-5 text-indigo-500" /> : <Smartphone className="w-5 h-5 text-blue-500" />}
                          <div>
                            <h4 className="font-medium text-gray-900">{method.name}</h4>
                            <p className="text-xs text-gray-500">
                              {method.enabled ? 'Enabled' : 'Not configured'} • Last used: {method.lastUsed ? new Date(method.lastUsed).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                        </div>
                        <Button variant={method.enabled ? 'outline' : 'primary'} size="sm">
                          {method.enabled ? 'Manage' : 'Setup'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="animate-in fade-in duration-300 space-y-6">
                <Card>
                  <CardHeader title="Privacy Preferences" icon={<Eye className="w-5 h-5 text-gray-400" />} />
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Profile Visibility</h4>
                        <p className="text-sm text-gray-500">Control who can see your profile details.</p>
                      </div>
                      <select className="form-select rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm">
                        <option>Public</option>
                        <option>Private</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Location Sharing</h4>
                        <p className="text-sm text-gray-500">Share your approximate location for job matching.</p>
                      </div>
                      <div className="flex items-center">
                        <input type="checkbox" defaultChecked className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-500 focus:ring-opacity-50" />
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="border border-red-200 bg-red-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Danger Zone
                  </h3>
                  <p className="text-sm text-red-600 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>

                  {!showDeleteConfirm ? (
                    <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Account
                    </Button>
                  ) : (
                    <div className="space-y-4 animate-in fade-in">
                      <p className="text-sm font-bold text-red-700">Type "DELETE" to confirm:</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          className="form-input rounded-md border-red-300 focus:border-red-500 focus:ring-red-500 w-full max-w-xs"
                          placeholder="DELETE"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                        />
                        <Button
                          className="bg-red-600 hover:bg-red-700 text-white"
                          disabled={deleteConfirmText !== 'DELETE'}
                          onClick={() => {
                            success('Account Deleted', 'Your account has been scheduled for deletion.');
                            setShowDeleteConfirm(false);
                          }}
                        >
                          Confirm Delete
                        </Button>
                        <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
